import { createSlice } from '@reduxjs/toolkit';
import { CallActivityTrackerData } from '../pages/DashboardV2/ActivityTracker/types';

export interface ILeaderboard {
  agentLevelRanking: number;
  maxLevel: number;
  cashCollectedInLast30Days: number;
  cashRequiredToReachNextLevel: number | null;
  todaysCashCollected?: number;
  noOfDaysCashCollected?: number;
  totalMonthlyCashCollected: number;
  totalCurrentMonthCashCollected: number;
  totalPrevMonthCashCollected: number;

  firstAllocationDate?: string;
  dateWhenRankingVisible?: string;

  promiseCount?: IPromiseCount;
  percentCompletedRecords: Array<IPercentCompleted>;
  levelConstructList: Array<ILevelConstruct>;
  callbridgeData: ICallBridgeData;

  monthlyPerformance: any;
  weeklyPerformance: any;

  currentMonthCashCollected: ICashCollectedBreakup[];
  prevMonthCashCollected: ICashCollectedBreakup[];
  monthlyCashCollectedBreakup: ICashCollectedBreakup[];

  isExpanded: boolean;
  leaderboardLoading: boolean;
  useOldIncentiveData?: boolean;
  useOldPrevMonthIncentiveData?: boolean;
  useOldMonthlyIncentiveData?: boolean;

  incentiveData?: IncentiveData;
  prevMonthIncentiveData?: IncentiveData;
  monthlyIncentiveData?: IncentiveData;

  slabBasedIncentiveData?: IncentiveData;
  prevMonthSlabBasedIncentiveData?: IncentiveData;
  monthlySlabBasedIncentiveData?: IncentiveData;
  activityCardData: IActivityCardData;
  dailyCallActivityData: CallActivityTrackerData;
}

export interface IActivityCardData {
  agentReferenceId: string;
  date: string;
  loginMetrics: {
    loginTimeStamp: string;
    longhornActiveMinutes: number;
    expectedActiveMinutes: number;
    loginStatus: string;
  };
  activityMetrics: {
    totalActiveMinutes: number;
    totalBreakMinutes: number;
    breakTimeByReason: {
      EVENING_BREAK: number;
      MORNING_BREAK: number;
      MEETING: number;
      TRAINING: number;
      LUNCH_BREAK: number;
      OTHERS: number;
    };
  };
}

export interface IPromiseCount {
  promiseYetToMature: number;
  promiseFulfilled: number;
  promiseBroken: number;
}

export type ICashCollectedBreakup = {
  label: string;
  value: number;
  valuePercent: number;
  fill?: string;
};

export interface ICallBridgeData {
  currentBusinessDate: string;
  successfulCallBridgeCount: number;
  successfulAmeyoAttemptedCount: number;
}

export interface IPercentCompleted {
  interactionStatus: string;
  paid: number;
  unpaid: number;
}

export interface ILevelConstruct {
  level: number;
  levelLowerLimit: number;
  levelUpperLimit: number;
}

export interface IPerformance {
  xaxisLabel: any;
  yaxisLabel: any;
  data: IDataSet[];
  xaxisDomainValues: Array<string>;
  hasLevelChanged: boolean;
}

export interface IDataSet {
  xaxis: string;
  cashCollectedInLast30Days: string;
  level: string | null;
  upperLevelCashCollectedInLast30Days: string | null;
  lowerLevelCashCollectedInLast30Days: string | null;
}

export interface IncentiveData {
  totalCashCollected?: number;
  thresholdForIncentive?: number;
  amountEligibleForIncentive?: number;
  incentivePercentage?: number;
  incentiveEarned?: number;
  total1To30DaysCashCollected?: number;
  total30PlusDaysCashCollected?: number;
  total1To30DaysIncentiveEarned?: number;
  total30PlusDaysIncentiveEarned?: number;
  totalIncentiveEarned?: number;
  slabBreakdown?: SlabBreakdown[];
  bufferCashCollected?: number;
  bufferIncentiveEarned?: number;
  buffer1To30DaysCashCollected?: number;
  buffer30PlusDaysCashCollected?: number;
  buffer1To30DaysIncentiveEarned?: number;
  buffer30PlusDaysIncentiveEarned?: number;
  nonBuffer30PlusIncentiveEarned?: number;
  nonBuffer1To30DaysIncentiveEarned?: number;
}

export interface SlabBreakdown {
  slabRank: number;
  slabLowerLimit: number;
  slabUpperLimit: number;
  incentivePercentage: number;
  cashCollectedInSlab: number;
  incentiveInSlab: number;
  isCurrentSlab: boolean;
}

const initialState = {
  agentLevelRanking: 0,
  maxLevel: 0,
  cashCollectedInLast30Days: 0,
  cashRequiredToReachNextLevel: 0,
  firstAllocationDate: '',
  percentCompletedRecords: [],
  levelConstructList: [
    {
      level: 0,
      levelLowerLimit: 0
    },
    {
      level: 0,
      levelLowerLimit: 0
    }
  ],
  isExpanded: false,
  leaderboardLoading: false,
  activityCardData: {},
  dailyCallActivityData: {}
} as unknown as ILeaderboard;

const COLOR_MAPPING = {
  '30+ Cases': '#60D8B3',
  EMIs: '#7CB5EC',
  Fees: '#FF8B53',
  Others: '#FFAE42'
};

const notificationSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    setLeaderboard(state, action) {
      const {
        agentLevelRanking,
        maxLevel,
        cashCollectedInLast30Days,
        cashRequiredToReachNextLevel,
        levelConstructList,
        firstAllocationDate,
        currentMonthCashCollected,
        prevMonthCashCollected,
        monthlyCashCollected,
        totalCurrentMonthCashCollected,
        totalPrevMonthCashCollected,
        todaysCashCollected,
        noOfDaysCashCollected,
        dateWhenRankingVisible,
        incentiveData,
        prevMonthIncentiveData,
        monthlyIncentiveData,
        slabBasedIncentiveData,
        prevMonthSlabBasedIncentiveData,
        monthlySlabBasedIncentiveData,
        useOldIncentiveData,
        useOldMonthlyIncentiveData,
        useOldPrevMonthIncentiveData
      } = action.payload;
      // todo make it action.payload
      state.agentLevelRanking = agentLevelRanking;
      state.maxLevel = maxLevel;
      state.todaysCashCollected = todaysCashCollected;
      state.noOfDaysCashCollected = noOfDaysCashCollected;
      state.dateWhenRankingVisible = dateWhenRankingVisible;
      state.cashCollectedInLast30Days = cashCollectedInLast30Days;
      state.cashRequiredToReachNextLevel = cashRequiredToReachNextLevel;
      state.levelConstructList = levelConstructList;
      state.firstAllocationDate = firstAllocationDate;
      state.currentMonthCashCollected = currentMonthCashCollected?.map(item => ({
        ...item,
        fill: COLOR_MAPPING[item.label as keyof typeof COLOR_MAPPING]
      }));
      state.prevMonthCashCollected = prevMonthCashCollected?.map(item => ({
        ...item,
        fill: COLOR_MAPPING[item.label as keyof typeof COLOR_MAPPING]
      }));
      state.monthlyCashCollectedBreakup = monthlyCashCollected?.monthlyCashCollectedBreakup?.map(
        item => ({
          ...item,
          fill: COLOR_MAPPING[item.label as keyof typeof COLOR_MAPPING]
        })
      );
      state.totalMonthlyCashCollected = monthlyCashCollected?.totalMonthlyCashCollected;
      state.totalCurrentMonthCashCollected = totalCurrentMonthCashCollected;
      state.totalPrevMonthCashCollected = totalPrevMonthCashCollected;
      state.incentiveData = incentiveData;
      state.prevMonthIncentiveData = prevMonthIncentiveData;
      state.monthlyIncentiveData = monthlyIncentiveData;
      state.slabBasedIncentiveData = slabBasedIncentiveData;
      state.prevMonthSlabBasedIncentiveData = prevMonthSlabBasedIncentiveData;
      state.monthlySlabBasedIncentiveData = monthlySlabBasedIncentiveData;
      state.leaderboardLoading = false;
      state.useOldIncentiveData = useOldIncentiveData;
      state.useOldMonthlyIncentiveData = useOldMonthlyIncentiveData;
      state.useOldPrevMonthIncentiveData = useOldPrevMonthIncentiveData;
    },
    setMonthlyPerformance(state, action) {
      let currentLevel = '';
      let hasLevelChanged = false;
      action?.payload?.data?.forEach((dataItem: IDataSet) => {
        if (dataItem.level) {
          if (currentLevel == '') {
            currentLevel = dataItem.level;
          }
          if (currentLevel != dataItem.level) {
            hasLevelChanged = true;
          }
        }
      });

      state.monthlyPerformance = {
        ...action.payload,
        hasLevelChanged
      };
    },
    setWeeklyPerformance(state, action) {
      let currentLevel = '';
      let hasLevelChanged = false;
      action?.payload?.data?.forEach((dataItem: IDataSet) => {
        if (dataItem.level) {
          if (currentLevel == '') {
            currentLevel = dataItem.level;
          }
          if (currentLevel != dataItem.level) {
            hasLevelChanged = true;
          }
        }
      });

      state.weeklyPerformance = {
        ...action.payload,
        hasLevelChanged
      };
    },
    setPercentCompletedCases(state, action) {
      state.percentCompletedRecords = action.payload;
    },
    setPromiseCount(state, action) {
      state.promiseCount = action.payload;
    },
    setCallbridgeData(state, action) {
      state.callbridgeData = action.payload;
    },
    setIsExpanded(state, action) {
      state.isExpanded = action.payload;
    },
    showLoader(state, action) {
      state.leaderboardLoading = action.payload;
    },
    setActivityCardData(state, action) {
      state.activityCardData = action.payload;
    },
    setDailyCallActivityData(state, action) {
      state.dailyCallActivityData = action.payload;
    }
  }
});

export const {
  setLeaderboard,
  setMonthlyPerformance,
  setWeeklyPerformance,
  setPercentCompletedCases,
  setPromiseCount,
  setCallbridgeData,
  setIsExpanded,
  showLoader,
  setActivityCardData,
  setDailyCallActivityData
} = notificationSlice.actions;

export default notificationSlice.reducer;
